import { useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { CHECKOUT_APPLY_PROMO_CODE, CHECKOUT_RESET_PROMO_CODE } from 'actions/actionConstants'
import { fetchMetasearchDiscount } from 'actions/MetasearchDiscountActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { getMetasearchDiscountAmount } from 'selectors/metasearchDiscountSelectors'
import { buildMetasearchDiscountRequestKey, hasMetasearchDiscountContext } from 'lib/metasearch/metasearchDiscountContext'
import { getPendingGhaVoucher } from 'lib/metasearch/pendingGhaVoucher'
import { serialiseOccupancy } from 'lib/search/occupancy'
import { isBedbankItem } from 'lib/checkout/checkoutUtils'
import getBedbankEnquiry from 'checkout/selectors/view/items/getBedbankEnquiry'

/**
 * Shows the GHA discount on the summary while the shopper is still a guest.
 *
 * The voucher itself cannot exist yet — it is assigned to a customer as it is created, and
 * `GuestContactDetailsSection` only registers an account once the shopper submits their email.
 * `/discount-resolve` needs no authentication though, so the amount is knowable well before the
 * voucher is. Writing it to `checkout.promo` in the shape svc-promo returns means the summary,
 * the breakdown rows and the totals all render it exactly as they render the real voucher, with
 * no second display path to keep in sync.
 *
 * Signed in means the real voucher is now possible and `useCreatePendingGhaVoucher` mints it.
 * The preview holds until that mint settles: the real promo replaces it in place, or failure
 * clears it to full price.
 */
// Stamped on preview items so the hook can tell its own promo from one it must not touch.
export const GHA_DISCOUNT_PREVIEW_MARKER = 'gha-discount-preview'

export function isGhaDiscountPreviewPromo(
  promo: { items?: ReadonlyArray<Pick<App.Checkout.ItemDiscount, 'itemInfoString'>> } | undefined,
): boolean {
  return !!promo?.items?.some(item => item.itemInfoString === GHA_DISCOUNT_PREVIEW_MARKER)
}

function getEnquiryDiscountPricing(enquiry: App.BedbankEnquiry | undefined) {
  if (!enquiry?.totals) return undefined
  const propertyFees = enquiry.totals.propertyFees ?? 0
  return {
    price: enquiry.totals.inclusive + propertyFees,
    propertyFees,
    currency: enquiry.currencyCode,
    mx: enquiry.mx,
    rateId: enquiry.roomRateId,
    calculationMethod: enquiry.calculationMethod,
  }
}

export default function usePendingGhaDiscountPreview(checkoutReady: boolean, mintFailed: boolean) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const isLoggedIn = useAppSelector(selectLoggedIn)
  // Matches GHA_DISCOUNT_VOUCHER_BREAKDOWN_TITLE in svc-connection-google. Restated rather than
  // fetched: the real voucher replaces this preview at registration, so its title always wins.
  const voucherBreakdownTitle = intl.formatMessage({
    id: '9bvif7R8mzkJ-cW2VMJ1',
    defaultMessage: 'Exclusive new member offer',
    description: 'Checkout summary voucher row title for the Google Hotel Ads new-member discount shown while the shopper is still a guest',
  })
  const currency = useAppSelector(state => state.checkout.cart.currencyCode)
  // Selecting the stable cart array and filtering in a memo: filtering inside the selector
  // returns a new array every render, which would re-fire the effect below on each one.
  const cartItems = useAppSelector(state => state.checkout.cart.items)
  const bedbankItems = useMemo(() => cartItems.filter(isBedbankItem), [cartItems])

  const previewApplied = useRef(false)

  // Read once: the offer page writes this before it navigates here, and nothing on checkout
  // changes it.
  const pendingVoucher = useMemo(getPendingGhaVoucher, [])
  // Read live, not stored: checkout re-creates the enquiry after a reload.
  const enquiry = useAppSelector(state => (
    pendingVoucher?.propertyId ? getBedbankEnquiry(state, pendingVoucher.propertyId) : undefined
  ))

  const discountInput = useMemo(() => {
    const pricing = getEnquiryDiscountPricing(enquiry)
    if (!pendingVoucher?.rooms?.length || !pricing) return undefined
    const input = {
      token: pendingVoucher.token,
      propertyId: pendingVoucher.propertyId,
      checkIn: pendingVoucher.checkIn,
      checkOut: pendingVoucher.checkOut,
      occupancy: serialiseOccupancy(pendingVoucher.rooms),
      rateRuleId: pendingVoucher.rateRuleId,
      ...pricing,
      advertisedPrice: pendingVoucher.advertisedPrice,
      advertisedCurrency: pendingVoucher.advertisedCurrency,
    }
    return hasMetasearchDiscountContext(input) ? input : undefined
  }, [enquiry, pendingVoucher])

  const requestKey = discountInput ? buildMetasearchDiscountRequestKey(discountInput) : undefined
  const previewing = !!discountInput && !isLoggedIn
  const discountAmount = useAppSelector(state => getMetasearchDiscountAmount(state, { enabled: previewing, requestKey }))
  const promo = useAppSelector(state => state.checkout.promo)
  const promoApplyError = useAppSelector(state => state.checkout.fetchingPromoError)

  useEffect(() => {
    if (!previewing || !discountInput) return

    dispatch(fetchMetasearchDiscount(discountInput))
  }, [discountInput, dispatch, previewing])

  useEffect(() => {
    if (!checkoutReady) return

    // A guest can choose to continue without an unsupported voucher. The hook's
    // initial snapshot remains for this mount, so check the live stash before reapplying.
    if (!isLoggedIn && !getPendingGhaVoucher()) {
      previewApplied.current = false
      return
    }

    if (promo && !isGhaDiscountPreviewPromo(promo)) {
      // Someone else's promo owns the slot — the real voucher landing, or a code the shopper
      // typed. Release: this hook never applies over, or resets, a promo it didn't write.
      previewApplied.current = false
      return
    }

    if (!previewing || discountAmount <= 0 || !bedbankItems.length) {
      if (!previewApplied.current) return
      if (!previewing) {
        // Logged in: hold through mint -> apply. The real promo ends the hold by taking the
        // slot above; failure ends it here. `fetchingPromoError` is any promo failure, not just
        // ours — a stray one ending the hold early only recreates the flash this hold removes.
        if (!mintFailed && !promoApplyError) return
      }
      previewApplied.current = false
      dispatch({ type: CHECKOUT_RESET_PROMO_CODE })
      return
    }

    if (promo && promo.voucherTotal === discountAmount) {
      // The slot already holds this preview (a foreign promo returned above), so there is
      // nothing to write — re-dispatching would change the promo's identity and re-fire this
      // effect forever. Reclaim ownership so the reset paths keep working after a remount.
      previewApplied.current = true
      return
    }

    const perItemAmount = discountAmount / bedbankItems.length
    // Typed rather than inlined into the dispatch: the dispatch argument is untyped, so nothing
    // would otherwise stop this from omitting a field the real svc-promo response always carries
    // and that consumers read without guarding.
    //
    // Field-for-field the shape svc-promo returns for this voucher (see the /api/promo/discount
    // response once the voucher exists). A GHA discount is a voucher application, never a promo
    // code: put the amount in `discountTotal`/`items[].discountAmount` instead and checkout
    // renders it through `SummaryItemDiscount` as "Promotion {amount} off" rather than through
    // the voucher row the real one uses.
    const promotion: App.Promotion = {
      // No promo code backs this — it stands in for a voucher that does not exist yet.
      id: '',
      code: '',
      discount: 0,
      discountTotal: 0,
      voucherTotal: discountAmount,
      creditTotal: 0,
      currency,
      type: 'none',
      maxDiscount: 0,
      minSpend: undefined,
      // Required by the type; no checkout consumer reads it off the promo.
      expiresAt: '',
      warning: undefined,
      allowedPaymentMethods: [],
      isCorporate: false,
      isFriendsAndFamily: false,
      hasBinNumbers: false,
      hideDiscountPercentage: false,
      promoToggles: [],
      popupMessage: undefined,
      loyaltyPointsBonusResultV1: {
        loyaltyBonusFilterMethod: 'none',
        resultCode: 'SUCCESS',
        loyaltyCalculationResponseItems: [],
      },
      items: bedbankItems.map<App.Checkout.ItemDiscount>(item => ({
        itemId: item.itemId,
        offerId: item.offerId,
        categoryBK: 'hotel',
        productBK: undefined,
        // Voucher amounts are independent of the promo-code discount and are summed separately
        // (`getAllItemsDiscountTotals`), so the line discount stays zero.
        discountAmount: 0,
        discountType: 'none',
        discountValue: 0,
        discountableTotal: discountInput?.price ?? 0,
        maxDiscount: undefined,
        hideDiscountPercentage: false,
        itemInfoString: GHA_DISCOUNT_PREVIEW_MARKER,
        appliedVoucher: [{
          // Empty where the real values only exist once the voucher and its owner do. One shared
          // id across lines keeps this a single voucher application, as svc-promo returns it.
          voucherId: '',
          voucherUserId: '',
          voucherRewardId: '',
          voucherName: '',
          voucherTitle: '',
          voucherDescription: '',
          voucherAmountDisplay: '',
          expiresAt: '',
          voucherBreakdownTitle,
          voucherToggles: ['is-hidden'],
          voucherCurrency: currency,
          voucherCurrencies: [currency],
          voucherHasMultipleRewardAmounts: false,
          rewardKind: 'discount',
          isStackableWithVouchers: false,
          isStackableWithPromoCodes: false,
          itemId: item.itemId,
          transactionKey: item.transactionKey,
          amount: perItemAmount,
          remainingAmountAfter: 0,
          theme: 'default',
          productType: 'generic',
        }],
      })),
    }

    previewApplied.current = true
    dispatch({ type: CHECKOUT_APPLY_PROMO_CODE, promotion })
  }, [bedbankItems, checkoutReady, currency, discountAmount, discountInput, dispatch, isLoggedIn, mintFailed, previewing, promo, promoApplyError, voucherBreakdownTitle])
}
